import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Grid, Stack, Box} from '@salesforce/retail-react-app/app/components/shared/ui'

import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'
import {Navigation} from 'swiper/modules'

import Header from './partials/header'
import Card from './partials/card'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const FullImageX4 = ({
    title,
    bodyCopy,
    bodyCopyTag = 'p',
    bodyCopyFontFamily = 'inter',
    titleTag = 'h2',
    titleColor = 'black',
    titleAlignment = 'center',
    titleFontFamily = 'caslon',
    headlineBackground = 'white',
    textPositionOnImage = 'bottom',
    minDisplay = '3',
    typeId,
    name,
    regions
}) => {
    const pdTagManager = usePdTagManager()
    const minDisplayPercent = 100 / parseInt(minDisplay, 10)
    const mobileGrid = minDisplay === '3' ? '1fr' : '1fr 1fr'
    useEffect(() => {
        pdTagManager.viewComponent(typeId, name, regions?.[0]?.components?.length)
    }, [])
    return (
        <Stack gap="0" display={{base: 'block', lg: 'none'}}>
            {title || bodyCopy ? (
                <Header
                    title={title}
                    bodyCopy={bodyCopy}
                    headlineBackground={headlineBackground}
                    titleAlignment={titleAlignment}
                    titleTag={titleTag}
                    titleColor={titleColor}
                    titleFontFamily={titleFontFamily}
                    bodyCopyTag={bodyCopyTag}
                    bodyCopyFontFamily={bodyCopyFontFamily}
                />
            ) : null}

            <Grid
                display={{base: 'grid', '2md': 'none'}}
                gridTemplateColumns={{
                    base: mobileGrid,
                    '2md': `repeat(${minDisplay <= 6 ? minDisplay : 6}, 1fr)`
                }}
                gridTemplateRows={{base: mobileGrid, '2md': '1fr'}}
                gap="0"
            >
                {regions?.[0]?.components?.map((card, index) => (
                    <Card
                        key={index}
                        {...card?.data}
                        isTextOverlay={textPositionOnImage === 'overlay'}
                        isFullImage={true}
                        cardsLength={regions?.[0]?.components?.length}
                        typeId={typeId}
                        index={index}
                    />
                ))}
            </Grid>

            <Box
                display={{base: 'none', '2md': 'block'}}
                sx={{
                    '.swiper': {
                        '&:not(.swiper-initialized)': {
                            paddingRight: 0,

                            '.swiper-wrapper': {
                                display: 'flex',
                                overflowX: 'hidden'
                            },
                            '.swiper-slide': {
                                flex: `0 0 ${minDisplayPercent}%`
                            }
                        },

                        '&:hover': {
                            '.swiper-button-prev, .swiper-button-next': {
                                opacity: 1
                            }
                        }
                    },
                    '.swiper-button-prev, .swiper-button-next': {
                        '--swiper-navigation-size': '45px',
                        display: {base: 'none', '2md': 'flex'},
                        top: '50%',
                        borderRadius: '5px',
                        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.20)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',

                        '&.swiper-button-disabled': {
                            display: 'none'
                        }
                    },
                    '.swiper-button-prev': {
                        left: '20px',
                        right: 'initial',
                        transform: 'translateY(-50%) rotate(180deg)'
                    },
                    '.swiper-button-next': {
                        right: '20px',
                        left: 'initial',
                        transform: 'translateY(-50%)'
                    }
                }}
            >
                <Swiper
                    navigation={true}
                    modules={[Navigation]}
                    breakpoints={{
                        768: {
                            slidesPerView: Math.min(minDisplay, 6)
                        },
                        1900: {
                            slidesPerView: Math.min(minDisplay, 8)
                        }
                    }}
                >
                    {regions?.[0]?.components?.map((card, index) => (
                        <SwiperSlide key={index}>
                            <Card
                                key={index}
                                {...card?.data}
                                isTextOverlay={textPositionOnImage === 'overlay'}
                                isFullImage={true}
                                cardsLength={regions?.[0]?.components?.length}
                                typeId={typeId}
                                index={index}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </Stack>
    )
}

FullImageX4.propTypes = {
    minDisplay: PropTypes.string,
    bodyCopy: PropTypes.string,
    bodyCopyTag: PropTypes.string,
    bodyCopyFontFamily: PropTypes.string,
    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleColor: PropTypes.string,
    titleAlignment: PropTypes.string,
    titleFontFamily: PropTypes.string,
    headlineBackground: PropTypes.string,
    textPositionOnImage: PropTypes.string,

    image1: PropTypes.string,
    imageAlt1: PropTypes.string,
    imageUrl1: PropTypes.string,
    imageTitle1: PropTypes.string,
    imageTitleColor1: PropTypes.string,
    imageTitleFontFamily1: PropTypes.string,
    imageTitleBackground1: PropTypes.string,

    image2: PropTypes.string,
    imageAlt2: PropTypes.string,
    imageUrl2: PropTypes.string,
    imageTitle2: PropTypes.string,
    imageTitleColor2: PropTypes.string,
    imageTitleFontFamily2: PropTypes.string,
    imageTitleBackground2: PropTypes.string,

    image3: PropTypes.string,
    imageAlt3: PropTypes.string,
    imageUrl3: PropTypes.string,
    imageTitle3: PropTypes.string,
    imageTitleColor3: PropTypes.string,
    imageTitleFontFamily3: PropTypes.string,
    imageTitleBackground3: PropTypes.string,

    image4: PropTypes.string,
    imageAlt4: PropTypes.string,
    imageUrl4: PropTypes.string,
    imageTitle4: PropTypes.string,
    imageTitleColor4: PropTypes.string,
    imageTitleFontFamily4: PropTypes.string,
    imageTitleBackground4: PropTypes.string,

    image5: PropTypes.string,
    imageAlt5: PropTypes.string,
    imageUrl5: PropTypes.string,
    imageTitle5: PropTypes.string,
    imageTitleColor5: PropTypes.string,
    imageTitleFontFamily5: PropTypes.string,
    imageTitleBackground5: PropTypes.string,

    image6: PropTypes.string,
    imageAlt6: PropTypes.string,
    imageUrl6: PropTypes.string,
    imageTitle6: PropTypes.string,
    imageTitleColor6: PropTypes.string,
    imageTitleFontFamily6: PropTypes.string,
    imageTitleBackground6: PropTypes.string,

    image7: PropTypes.string,
    imageAlt7: PropTypes.string,
    imageUrl7: PropTypes.string,
    imageTitle7: PropTypes.string,
    imageTitleColor7: PropTypes.string,
    imageTitleFontFamily7: PropTypes.string,
    imageTitleBackground7: PropTypes.string,

    image8: PropTypes.string,
    imageAlt8: PropTypes.string,
    imageUrl8: PropTypes.string,
    imageTitle8: PropTypes.string,
    imageTitleColor8: PropTypes.string,
    imageTitleFontFamily8: PropTypes.string,
    imageTitleBackground8: PropTypes.string,

    typeId: PropTypes.string,
    regions: PropTypes.any,
    name: PropTypes.any
}

export default FullImageX4
